import { useState } from 'react';
import { Award } from 'lucide-react';
import { User } from "@/types/user";
import { RewardsStatusData, ClaimRewardResponse, GameType } from "@/types/rewards";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/app/lib/axios";
import { useAuth } from '@/app/hooks/context/AuthContext';
import { BonusCard } from '@/app/components/Navbar/Profile/Rewards/BonusCard';

interface RewardsTabProps {
    user: User;
    isKickProvider: boolean;
}

export function RewardsTab({ isKickProvider }: RewardsTabProps) {
    const [bonusAmountDaily, setBonusAmountDaily] = useState<number | null>(null);
    const [bonusAmountWeekly, setBonusAmountWeekly] = useState<number | null>(null);
    const [bonusAmountMonthly, setBonusAmountMonthly] = useState<number | null>(null);
    const [claimingType, setClaimingType] = useState<GameType | null>(null);
    const queryClient = useQueryClient();
    const { refreshUser } = useAuth();

    const { data: rewardsData, isLoading } = useQuery<RewardsStatusData>({
        queryKey: ['rewardsStatus'],
        queryFn: async () => {
            const response = await api.get('rewards/status');
            return response.data;
        },
        enabled: isKickProvider,
        refetchOnWindowFocus: true,
    });

    const claimMutation = useMutation({
        mutationFn: async (gameType: GameType) => {
            const response = await api.post('rewards/claim', {
                gameType
            });
            return response.data as ClaimRewardResponse;
        },
        onSuccess: (data) => {
            if (data.status) {
                refreshUser();
                queryClient.invalidateQueries({ queryKey: ['user'] });
                queryClient.invalidateQueries({ queryKey: ['rewardsStatus'] });
                queryClient.invalidateQueries({ queryKey: ['userStatistics'] });
            }
            setClaimingType(null);
        },
        onError: () => {
            setClaimingType(null);
        }
    });

    const getBonusAmountSetter = (gameType: GameType) => {
        const setters: Record<string, React.Dispatch<React.SetStateAction<number | null>>> = {
            daily: setBonusAmountDaily,
            weekly: setBonusAmountWeekly,
            monthly: setBonusAmountMonthly,
        };
        return setters[gameType];
    };

    const collectBonus = async (gameType: GameType, canClaim: boolean) => {
        if (!canClaim || claimMutation.isPending) return;

        setClaimingType(gameType);
        const setBonusAmount = getBonusAmountSetter(gameType);

        claimMutation.mutate(
            gameType,
            {
                onSuccess: (data) => {
                    if (data.status && data.reward !== undefined) {
                        setBonusAmount(data.reward);
                    }
                }
            }
        );
    };

    if (!isKickProvider) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <div className="bg-yellow-900/50 border border-yellow-500 rounded-xl p-6 text-center">
                    <Award className="text-yellow-400 mx-auto mb-3" size={48} />
                    <h3 className="font-bold text-xl text-white mb-2">Kick Providers Only</h3>
                    <p className="text-xs text-yellow-200">Rewards are available for Kick providers only.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6 max-w-2xl mx-auto">
                <div className="text-center text-xs text-gray-400">Loading…</div>
            </div>
        );
    }

    const dailyReward = rewardsData?.games.daily;
    const weeklyReward = rewardsData?.games.weekly;
    const monthlyReward = rewardsData?.games.monthly;

    const bonuses = [
        {
            gameType: 'daily' as GameType,
            title: 'Daily Bonus',
            description: 'Once every day',
            imageSrc: '/banners/gift_banner_blue.webp',
            canClaim: dailyReward ? !dailyReward.claimed : false,
            isCollecting: claimingType === 'daily',
            progress: 0,
            bonusAmount: bonusAmountDaily,
            secondsUntilClaim: dailyReward?.seconds_until_next_claim ?? 0,
            colorScheme: {
                gradient: 'from-blue-600 via-blue-800',
                progressBar: 'from-blue-500 to-blue-400',
                button: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
                successBg: 'bg-blue-900/50',
                successText: 'text-blue-300',
            },
        },
        {
            gameType: 'weekly' as GameType,
            title: 'Weekly Bonus',
            description: 'Once every week',
            imageSrc: '/banners/gift_banner_green.webp',
            canClaim: weeklyReward ? !weeklyReward.claimed : false,
            isCollecting: claimingType === 'weekly',
            progress: 0,
            bonusAmount: bonusAmountWeekly,
            secondsUntilClaim: weeklyReward?.seconds_until_next_claim ?? 0,
            colorScheme: {
                gradient: 'from-emerald-600 via-emerald-800',
                progressBar: 'from-green-500 to-green-400',
                button: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
                successBg: 'bg-green-900/50',
                successText: 'text-green-300',
            },
        },
        {
            gameType: 'monthly' as GameType,
            title: 'Monthly Bonus',
            description: 'Once every month',
            imageSrc: '/banners/gift_banner_purple.webp',
            canClaim: monthlyReward ? !monthlyReward.claimed : false,
            isCollecting: claimingType === 'monthly',
            progress: 0,
            bonusAmount: bonusAmountMonthly,
            secondsUntilClaim: monthlyReward?.seconds_until_next_claim ?? 0,
            colorScheme: {
                gradient: 'from-purple-600 via-purple-800',
                progressBar: 'from-purple-500 to-purple-400',
                button: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
                successBg: 'bg-purple-900/50',
                successText: 'text-purple-300',
            },
        },
    ];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {bonuses.map((bonus) => (
                    <BonusCard
                        key={bonus.gameType}
                        {...bonus}
                        onCollect={() => collectBonus(bonus.gameType, bonus.canClaim)}
                    />
                ))}
            </div>

            <div className="bg-gray-800/50 rounded-xl p-6">
                <h3 className="text-sm font-bold uppercase tracking-wide text-green-400 mb-4">Reward probabilities</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wide text-blue-400 mb-2">Daily Bonus</h3>
                        <span className="text-gray-400 text-xs">
                            You can get randomly from 1 to 10 Kick Points per claim.
                        </span>
                    </div>
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wide text-green-400 mb-2">Weekly Bonus</h3>
                        <span className="text-gray-400 text-xs">
                            You can get randomly from 1 to 25 Kick Points per claim.
                        </span>
                    </div>
                    <div>
                        <h3 className="text-xs font-bold uppercase tracking-wide text-purple-400 mb-2">Monthly Bonus</h3>
                        <span className="text-gray-400 text-xs">
                            You can get randomly from 3 to 50 Kick Points per claim.
                        </span>
                    </div>
                </div>
                <div className="mt-6 pt-4 border-t border-white/5">
                    <p className="text-gray-400 text-xs">
                        ℹ️ These are temporary bonuses. In the future, bonus amounts will depend on interactions in streams. The more you interact, the more you get!
                    </p>
                </div>
            </div>
        </div>
    );
}